import React from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { MapPin, Users, Share2, Calendar } from 'lucide-react';

const Home = () => {
  const { isAuthenticated } = useSelector(state => state.auth);

  const features = [
    {
      icon: <MapPin className="h-8 w-8 text-primary-600" />,
      title: 'Interactive Maps',
      description: 'Plan your routes with integrated Google Maps and location search.'
    },
    {
      icon: <Users className="h-8 w-8 text-primary-600" />,
      title: 'Collaborative Planning',
      description: 'Invite friends and family to plan trips together in real-time.'
    },
    {
      icon: <Share2 className="h-8 w-8 text-primary-600" />,
      title: 'Easy Sharing',
      description: 'Share your itineraries with public links and inspire others.'
    },
    {
      icon: <Calendar className="h-8 w-8 text-primary-600" />,
      title: 'Day-wise Planning',
      description: 'Organize your trip day by day with detailed itineraries.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white animate-fade-in">
        <div className="max-w-7xl mx-auto px-4 py-20">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Plan Your Perfect Trip
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-primary-100">
              Create, collaborate, and share amazing travel experiences with friends and family.
            </p>
            <div className="space-x-4">
              {!isAuthenticated ? (
                <>
                  <Link to="/register" className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                    Get Started
                  </Link>
                  <Link to="/login" className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors">
                    Sign In
                  </Link>
                </>
              ) : (
                <Link to="/dashboard" className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
                  Go to Dashboard
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Plan
            </h2>
            <p className="text-xl text-gray-600">
              Powerful features to make trip planning effortless and fun.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-50 py-20">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Ready to Start Planning?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join thousands of travelers who trust TravelPlanner for their adventures.
          </p>
          {!isAuthenticated && (
            <Link to="/register" className="btn-primary text-lg px-8 py-3">
              Create Your First Trip
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;